import { STATUS_LABEL } from './OverviewTable'

/**
 * The status filter offered by the Overview tab.
 *
 * WHAT THIS BUYS, stated precisely, because the obvious justification is no longer the true
 * one. The complaint was a first page of archived promotions showing zeros in every column —
 * but the default ordering is now Most applied, descending, so zero-usage rows already sink
 * to the last page on their own. Ordering cannot touch the TOTALS: those count every
 * promotion the filter matches, and no arrangement changes a sum. So an operator reading
 * "412 applied across all promotions" is reading a figure that includes campaigns retired
 * two years ago, and the only way to make that number mean "current campaigns" is to filter.
 *
 * The filter NARROWS WITHIN the caller's provider scope and cannot widen it — loyalty
 * AND-composes it after the scope partition. No selection here reaches a row the operator
 * could not already see.
 *
 * Options are derived from STATUS_LABEL rather than restated, so a status added there cannot
 * be missing here. That is the one direction this derivation is safe in: production code
 * sharing a single constant. It would be the wrong shape in a test, where an assertion whose
 * expected value follows its subject stops constraining anything.
 */
export interface PromotionStatusFilterOption {
  key: string
  label: string
}

/**
 * The "any status" sentinel, deliberately NOT a backend enum value — the query omits the
 * `status` argument entirely when this is selected. The same sentinel and the same reasoning
 * are already in PromotionsFilter on the Promotions list; this is that convention, reused.
 */
export const ANY_STATUS = ''

export const PROMOTION_STATUS_FILTERS: PromotionStatusFilterOption[] = [
  { key: ANY_STATUS, label: 'All statuses' },
  ...Object.keys(STATUS_LABEL).map((key) => ({ key, label: STATUS_LABEL[key] }))
]

/**
 * Opens on Active.
 *
 * NAMED AND ISOLATED so changing it is one line, because it is a genuine judgement call and
 * not an obvious one. Active answers "what is working right now", which is what a performance
 * dashboard is usually opened for. The cost is that an EXPIRED campaign — often exactly the
 * one being reviewed, after it has finished — is hidden until the operator changes this
 * control. "All statuses" is one selection away and the control is adjacent to the totals it
 * governs, so the cost is visible and recoverable rather than silent.
 */
export const DEFAULT_PROMOTION_STATUS_FILTER = 'Active'

/**
 * The value to send as the `status` argument, or undefined to omit it.
 *
 * Anything unrecognised returns undefined — i.e. it degrades to "every status" rather than
 * to the default. An unknown key means the screen's state and this list have diverged, and
 * showing everything is the only answer that is not quietly wrong: silently substituting
 * Active would show a filtered set under a control that does not say it is filtering.
 */
export function promotionStatusFilterFor(key: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(STATUS_LABEL, key)
    ? key
    : undefined
}

/**
 * Every label this module hands to getTranslate, for the localization suite to assert on.
 *
 * Exported rather than retyped there: these reach getTranslate as `option.label` — a
 * VARIABLE — so the literal scan cannot see them, and an untranslated one would render an
 * English word inside an otherwise Persian control with nothing failing to say so. Same
 * blind spot, and same remedy, as the skip-reason labels.
 */
export const ALL_PROMOTION_STATUS_FILTER_LABELS = PROMOTION_STATUS_FILTERS.map(
  (o) => o.label
)
